import {useCallback, useEffect, useState} from "react";
import type {FormEvent} from "react";
import {
  createProgram, listPrograms, listProducts, listWorkPackageApprovers,
  readProgramRegistryActions, transitionProgram, updateProgram,
  type ApproverIdentity, type AvailableAction, type Product,
  type Program, type ProgramDraft
} from "../../api/client";
import {useAuth} from "../../auth/AuthContext";
import {AsyncState} from "../../components/AsyncState";
import {RegistryShell} from "../../components/Registry";
import {StatusBadge} from "../../components/StatusBadge";

const blank: ProgramDraft = {
  key: "", title: "", objective: "", ownerOrganization: "",
  namedApprover: "", productRelationships: []
};

export function ProgramRegistry() {
  const {authorizedFetch, user} = useAuth();
  const [items, setItems] = useState<Program[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Program | null>(null);
  const [actions, setActions] = useState<AvailableAction[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [approvers, setApprovers] = useState<ApproverIdentity[]>([]);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<ProgramDraft>(blank);
  const mayAuthor = actions.some(action =>
    action.actionId === "CREATE_PROGRAM" && action.available
  );
  const refresh = useCallback(async (query = "", signal?: AbortSignal) => {
    setLoading(true);
    try {
      setItems(await listPrograms(authorizedFetch, query, signal));
      setError("");
    } catch (problem) {
      if (!signal?.aborted) setError(message(problem));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [authorizedFetch]);
  useEffect(() => {
    const controller = new AbortController();
    void Promise.all([
      refresh("", controller.signal),
      readProgramRegistryActions(authorizedFetch, controller.signal).then(setActions),
      listProducts(authorizedFetch, "", controller.signal).then(values =>
        setProducts(values.filter(value => value.lifecycle === "ACTIVE"))),
      listWorkPackageApprovers(authorizedFetch, controller.signal).then(setApprovers)
    ]).catch(problem => {
      if (!controller.signal.aborted) setError(message(problem));
    });
    return () => controller.abort();
  }, [authorizedFetch, refresh]);
  const closeForm = () => {
    setDraft(blank);
    setEditing(null);
    setCreating(false);
  };
  const edit = (program: Program) => {
    setEditing(program);
    setDraft({
      key: program.key,
      title: program.title,
      objective: program.objective,
      ownerOrganization: program.ownerOrganization,
      namedApprover: program.namedApprover,
      productRelationships: program.productRelationships
    });
    setCreating(true);
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const saved = editing
        ? await updateProgram(authorizedFetch, editing, draft)
        : await createProgram(authorizedFetch, draft);
      setItems(current => [saved, ...current.filter(item => item.id !== saved.id)]);
      closeForm();
      setError("");
    } catch (problem) {
      setError(message(problem));
    } finally {
      setBusy(false);
    }
  };
  const transition = async (program: Program,
    action: "validate" | "submit" | "activate" | "reject" | "return-for-revision"
      | "revise" | "cancel" | "hold" | "resume" | "complete" | "archive") => {
    const rationale = action === "activate" || action === "reject"
      || action === "return-for-revision" || action === "cancel"
      || action === "hold" || action === "resume" || action === "complete"
      || action === "archive" || action === "revise"
      ? window.prompt(`${action} rationale`)?.trim() ?? "" : "";
    if (!["validate", "submit"].includes(action) && !rationale) return;
    setBusy(true);
    try {
      const saved = await transitionProgram(authorizedFetch, program, action, rationale);
      setItems(current => current.map(item => item.id === saved.id ? saved : item));
      setError("");
    } catch (problem) { setError(message(problem)); } finally { setBusy(false); }
  };
  return <RegistryShell title="Programs"
    description="Live governed Programs in the authenticated tenant, company, and Business Unit. Product relationships remain typed and same-scope."
    tools={<><form className="registry-search" role="search" onSubmit={event => {event.preventDefault(); void refresh(search);}}>
      <label htmlFor="program-search">Search Programs</label><input id="program-search" value={search} onChange={event => setSearch(event.target.value)} />
      <button type="submit">Search</button></form>
      {mayAuthor && <button className="primary-action" type="button" onClick={() => {
        setEditing(null);
        setDraft({...blank, ownerOrganization: user?.company ?? ""});
        setCreating(true);
      }}>Create Program Draft</button>}</>}>
    {error && <p className="form-message error" role="alert">{error}</p>}
    {creating && <form className="program-draft panel" onSubmit={event => void submit(event)}>
      <header><div><p className="eyebrow">Governed authoring</p><h2>{editing ? `Edit ${editing.key}` : "Create Program Draft"}</h2></div><StatusBadge value="DRAFT" /></header>
      <div className="form-grid"><label>Key<input required pattern="[A-Z][A-Z0-9-]{2,59}" value={draft.key} onChange={event => setDraft({...draft, key: event.target.value.toUpperCase()})} /></label>
        <label>Title<input required value={draft.title} onChange={event => setDraft({...draft, title: event.target.value})} /></label>
        <label className="span-2">Objective<textarea required rows={4} value={draft.objective} onChange={event => setDraft({...draft, objective: event.target.value})} /></label>
        <label>Owner organization<input required value={draft.ownerOrganization} onChange={event => setDraft({...draft, ownerOrganization: event.target.value})} /></label>
        <label>Named approver<select required value={draft.namedApprover} onChange={event => setDraft({...draft,namedApprover:event.target.value})}><option value="">Select different human</option>{approvers.map(item => <option key={item.username} value={item.username}>{item.username} — {item.displayName}</option>)}</select></label>
        <label>Primary Product<select required value={draft.productRelationships[0]?.productId ?? ""} onChange={event => setDraft({...draft,productRelationships:event.target.value?[{productId:event.target.value,relationshipType:"PRIMARY"}]:[]})}><option value="">Select ACTIVE Product</option>{products.map(product => <option key={product.id} value={product.id}>{product.key} — {product.name}</option>)}</select></label></div>
      <p className="field-note">Program validation requires at least one same-scope ACTIVE Product. No fixture Product may be attached.</p>
      <div className="form-actions"><button type="button" onClick={closeForm}>Cancel</button><button className="primary-action" type="submit" disabled={busy}>{busy ? "Saving…" : editing ? "Save exact Draft" : "Create Draft"}</button></div>
    </form>}
    {loading ? <AsyncState kind="loading" detail="Reading Programs from the live scoped store." />
      : items.length === 0 ? <AsyncState kind="empty" detail="No Program exists in this company and Business Unit. No fixture Program is substituted." />
      : <div className="registry-table"><table><thead><tr><th>Program</th><th>Owner</th><th>Lifecycle</th><th>Version</th><th>Products</th><th>Actions</th></tr></thead>
        <tbody>{items.map(program => <tr key={program.id}><td><a href={`/programs/${program.id}`}>{program.key}</a><span>{program.title}</span><a className="roadmap-entry" href={`/programs/${program.id}/roadmap`}>Roadmap</a></td>
          <td>{program.ownerOrganization}</td><td><StatusBadge value={program.lifecycle} /></td><td>v{program.version}</td><td>{program.productRelationships.length}</td><td className="row-actions">
            {mayAuthor && program.lifecycle === "DRAFT" && <button disabled={busy} onClick={() => edit(program)}>Edit</button>}
            {program.lifecycle === "DRAFT" && <button disabled={busy} onClick={() => void transition(program,"validate")}>Validate</button>}
            {program.lifecycle === "VALIDATED" && <button disabled={busy} onClick={() => void transition(program,"submit")}>Submit</button>}
            {program.lifecycle === "SUBMITTED" && program.namedApprover === user?.username && <button disabled={busy} onClick={() => void transition(program,"activate")}>Approve & activate</button>}
            {program.lifecycle === "REVISION_REQUIRED" && <button disabled={busy} onClick={() => void transition(program,"revise")}>Open revision</button>}
            {program.lifecycle === "ACTIVE" && <button disabled={busy} onClick={() => void transition(program,"hold")}>Hold</button>}
            {program.lifecycle === "ON_HOLD" && <button disabled={busy} onClick={() => void transition(program,"resume")}>Resume</button>}
            {(program.lifecycle === "ACTIVE" || program.lifecycle === "ON_HOLD") && <button disabled={busy} onClick={() => void transition(program,"complete")}>Complete</button>}
            {(["DRAFT", "VALIDATED", "SUBMITTED", "ACTIVE", "ON_HOLD", "REVISION_REQUIRED"].includes(program.lifecycle)) && <button disabled={busy} onClick={() => void transition(program,"cancel")}>Cancel</button>}
            {(["COMPLETED", "CANCELLED", "REJECTED"].includes(program.lifecycle)) && <button disabled={busy} onClick={() => void transition(program,"archive")}>Archive</button>}
          </td></tr>)}</tbody></table></div>}
  </RegistryShell>;
}

function message(problem: unknown) {
  return problem instanceof Error ? problem.message : "Program operation failed";
}
